import { lazy, Suspense, useEffect } from 'react'
import { config } from './config'
import axios from 'axios'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import ErrorBoundary from './ErrorBoundary'
import { Toaster } from 'react-hot-toast';

// Main:
const FooterMain = lazy(() => import('./main/main-components/FooterMain'));
const NavbarMain = lazy(() => import('./main/main-components/NavbarMain'));
const PaymentSuccessPageMain = lazy(() => import('./main/main-pages/PaymentSuccessPageMain'));
const HomePageMain = lazy(() => import('./main/main-pages/HomePageMain'));
const AboutPageMain = lazy(() => import('./main/main-pages/AboutPageMain'));
const PolicyMain = lazy(() => import('./main/main-pages/PolicyMain'));
const TermsMain = lazy(() => import('./main/main-pages/TermsMain'));
const HelpPageMain = lazy(() => import('./main/main-pages/HelpPageMain'));
const LoginPageMain = lazy(() => import('./main/auth/LoginPageMain'));
const RegisterPageMain = lazy(() => import('./main/auth/RegisterPageMain'));
const ProtectedRouteMain = lazy(() => import('./main/auth/ProtectedRouteMain'));
const VerifyEmailPage = lazy(() => import('./main/auth/VerifyEmailPage'));
const ForgotPasswordPage = lazy(() => import('./main/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./main/auth/ResetPasswordPage'));
const TwoFactorSetupPage = lazy(() => import('./main/auth/TwoFactorSetupPage'));
const TwoFactorVerifyPage = lazy(() => import("./main/auth/TwoFactorVerifyPage"));
const AdminPage = lazy(() => import('./main/admin/AdminPage'));

// LoadingSpinner component for showing loading state
const LoadingSpinner = () => {
  return (
    <div className="min-h-screen bg-gray-900 dark:bg-gray-900 flex items-center justify-center">
      <div className="flex justify-center items-center space-x-2 mx-auto">
        <div className="w-8 h-8 bg-[#019eff] rounded-full animate-bounce"></div>
        <div className="w-8 h-8 bg-[#019eff] rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
        <div className="w-8 h-8 bg-[#019eff] rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
      </div>
    </div>
  );
};

function App() {
  const isCreatorCustomDomain = window.location.host !== 'www.ecom.com' && window.location.host !== 'ecom.com' && window.location.host !== 'localhost:1420'
  let no_params_main_user_id = ''
  if (isCreatorCustomDomain) {
    no_params_main_user_id = 'fetch-me'
  } else {
    const url_parts = window.location.pathname.split('/')
    const url_part_one = url_parts[1]
    const url_part_two = url_parts[2]
    if (url_part_one === 'creator' && url_part_two) {
      no_params_main_user_id = url_part_two
    }
  }
  if (isCreatorCustomDomain) no_params_main_user_id = 'fetch-me'
  const creatorLink = isCreatorCustomDomain ? '' : `/creator/${no_params_main_user_id}`

  useEffect(() => {
    if (isCreatorCustomDomain) {
      axios.get(`${config.server}/lookup/${window.location.host}`).then(res => {
        console.log('%c Domain Lookup Results:', 'background: #222; color: #bada55', res.data)
      }).catch(error => {
        console.log('%c Domain Lookup Error:', 'background: #222; color: #ff6b6b', error)
      })
    }
  }, [isCreatorCustomDomain])

  return (
    <ErrorBoundary>
      <Routes>
        {/* Main: */}
        {!isCreatorCustomDomain && (
          <>
            <Route path='/' element={
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <div className='min-h-screen bg-gray-900 dark:bg-gray-900 flex flex-col montserrat-bold'>
                    <div className='grow'>
                      <div className='fixed top-0 left-0 right-0 z-50'>
                        <NavbarMain />
                      </div>
                      <HomePageMain />
                    </div>
                  </div>
                </Suspense>
              </ErrorBoundary>
            } />
            <Route path='/about' element={
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <div className='min-h-screen bg-gray-900 dark:bg-gray-900 flex flex-col montserrat-bold'>
                    <div className='grow'>
                      <div className='fixed top-0 left-0 right-0 z-50'>
                        <NavbarMain />
                      </div>
                      <AboutPageMain />
                      <FooterMain />
                    </div>
                  </div>
                </Suspense>
              </ErrorBoundary>
            } />
            <Route path='/login' element={
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <div className='min-h-screen bg-gray-900 dark:bg-gray-900 flex flex-col'>
                    <div className='grow'>
                      <div className='fixed top-0 left-0 right-0 z-50'>
                        <NavbarMain />
                      </div>
                      <LoginPageMain />
                      <FooterMain />
                    </div>
                  </div>
                </Suspense>
              </ErrorBoundary>
            } />
            <Route path='/register' element={
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <div className='min-h-screen bg-gray-900 dark:bg-gray-900 flex flex-col'>
                    <div className='grow'>
                      <div className='fixed top-0 left-0 right-0 z-50'>
                        <NavbarMain />
                      </div>
                      <RegisterPageMain />
                      <FooterMain />
                    </div>
                  </div>
                </Suspense>
              </ErrorBoundary>
            } />
            <Route path='/verify-email' element={
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <div className='min-h-screen bg-gray-900 dark:bg-gray-900 flex flex-col'>
                    <div className='grow'>
                      <div className='fixed top-0 left-0 right-0 z-50'>
                        <NavbarMain />
                      </div>
                      <VerifyEmailPage />
                      <FooterMain />
                    </div>
                  </div>
                </Suspense>
              </ErrorBoundary>
            } />
            <Route path='/policy' element={
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <div className='min-h-screen bg-gray-900 dark:bg-gray-900 flex flex-col'>
                    <div className='grow'>
                      <div className='fixed top-0 left-0 right-0 z-50'>
                        <NavbarMain />
                      </div>
                      <PolicyMain />
                      <FooterMain />
                    </div>
                  </div>
                </Suspense>
              </ErrorBoundary>
            } />
            <Route path='/terms' element={
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <div className='min-h-screen bg-gray-900 dark:bg-gray-900 flex flex-col'>
                    <div className='grow'>
                      <div className='fixed top-0 left-0 right-0 z-50'>
                        <NavbarMain />
                      </div>
                      <TermsMain />
                      <FooterMain />
                    </div>
                  </div>
                </Suspense>
              </ErrorBoundary>
            } />
            <Route path='/help' element={
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <div className='min-h-screen bg-gray-900 dark:bg-gray-900 flex flex-col'>
                    <div className='grow'>
                      <div className='fixed top-0 left-0 right-0 z-50'>
                        <NavbarMain />
                      </div>
                      <HelpPageMain />
                      <FooterMain />
                    </div>
                  </div>
                </Suspense>
              </ErrorBoundary>
            } />
            <Route path='/payment/success' element={
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <div className='min-h-screen bg-gray-900 dark:bg-gray-900 flex flex-col'>
                    <div className='grow'>
                      <div className='fixed top-0 left-0 right-0 z-50'>
                        <NavbarMain />
                      </div>
                      <PaymentSuccessPageMain />
                      <FooterMain />
                    </div>
                  </div>
                </Suspense>
              </ErrorBoundary>
            } />
            <Route path='/admin' element={
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <div className='min-h-screen bg-gray-900 dark:bg-gray-900 flex flex-col'>
                    <div className='grow'>
                      <div className='fixed top-0 left-0 right-0 z-50'>
                        <NavbarMain />
                      </div>
                      <AdminPage />
                      <FooterMain />
                    </div>
                  </div>
                </Suspense>
              </ErrorBoundary>
            } />
          </>
        )}
        {/* 404 */}
        <Route path='*' element={
          <ErrorBoundary>
            <Suspense fallback={<LoadingSpinner />}>
              <div className='min-h-screen bg-black flex flex-col items-center justify-center text-center p-4'>
                <h1 className='text-5xl font-bold mb-4 animate-pulse text-transparent bg-clip-text bg-linear-to-r from-purple-400 to-pink-600'>404</h1>
                <p className='text-xl text-gray-400 mb-8'>Oops! Looks like you've ventured into unknown territory.</p>
                <button onClick={() => (window.location.href = `${creatorLink}`)} className='px-6 py-3 rounded-lg bg-linear-to-r from-purple-500 to-pink-500 text-white font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-300'>
                  Back
                </button>
              </div>
            </Suspense>
          </ErrorBoundary>
        } />
        <Route path="/forgot-password" element={
          <ErrorBoundary>
            <Suspense fallback={<LoadingSpinner />}>
              <ForgotPasswordPage />
            </Suspense>
          </ErrorBoundary>
        } />
        <Route path="/reset-password" element={
          <ErrorBoundary>
            <Suspense fallback={<LoadingSpinner />}>
              <ResetPasswordPage />
            </Suspense>
          </ErrorBoundary>
        } />
        <Route path="/verify-2fa" element={
          <ErrorBoundary>
            <Suspense fallback={<LoadingSpinner />}>
              <TwoFactorVerifyPage />
            </Suspense>
          </ErrorBoundary>
        } />
        <Route path='/2fa-setup' element={
          <ErrorBoundary>
            <Suspense fallback={<LoadingSpinner />}>
              <div className='min-h-screen bg-gray-900 dark:bg-gray-900 flex flex-col'>
                <div className='grow'>
                  <div className='fixed top-0 left-0 right-0 z-50'>
                    <NavbarMain />
                  </div>
                  <ProtectedRouteMain>
                    <TwoFactorSetupPage />
                  </ProtectedRouteMain>
                  <FooterMain />
                </div>
              </div>
            </Suspense>
          </ErrorBoundary>
        } />
      </Routes>
    </ErrorBoundary>
  )
}

function AppRouter() {
  return (
    <ErrorBoundary>
      <Router>
        <ErrorBoundary>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 5000,
              style: {
                zIndex: 9999,
              },
              // Custom styling for consistent toast appearance
              success: {
                style: {
                  background: '#10B981',
                  color: 'white',
                  padding: '16px',
                  borderRadius: '8px',
                },
                iconTheme: {
                  primary: 'white',
                  secondary: '#10B981',
                },
              },
            }}
          />
        </ErrorBoundary>
        <App />
      </Router>
    </ErrorBoundary>
  )
}

export default AppRouter
